import {
    Paper,
    Typography,
    Box,
    Stack,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    TextField,
    MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SpeedIcon from "@mui/icons-material/Speed";
import { useState } from "react";
import { ResponsivePie } from "@nivo/pie";

const competenceColors = {
    "Spostrzegawczość": "#99d5ff",
    "Refleks": "#ffa8a8",
    "Koncentracja": "#c3b1ff",
    "Pamięć": "#ffe08a",
    "Koordynacja ruchowa": "#8de0cc",
    "Działanie pod presją czasu": "#ffbe8a",
    "Dokładność": "#b9df8a",
    "Wykorzystanie wiedzy": "#b2c7ef",
    "Podejmowanie decyzji": "#f5a9d0",
    "Logiczne myślenie": "#83d8e6",
    "Wyobraźnia": "#d8b5df",
    "Kreatywność": "#e3cd9e",
    "Cierpliwość": "#c4d4d9",
};

export default function MinigamesAnalysis() {
    const [filter, setFilter] = useState("all");
    const [selectedGame, setSelectedGame] = useState("all");

    const games = [
        {
            title: "Pacnij Kreta",
            difficulty: "easy",
            text: "Jedna z najlepiej ocenianych minigier. Większość zespołów osiągała wysoką skuteczność już po kilku próbach. Polega na tym, aby jak najszybciej pacnąć wszystkie wysunięte krety. Punkty zdobywa się za każde trafienie, a traci się wraz z upływem czasu. Gra ćwiczy spostrzegawczość, refleks i szybkie reagowanie.",
            competences: ["Spostrzegawczość", "Refleks", "Koordynacja ruchowa", "Działanie pod presją czasu"],
        },
        {
            title: "Gra w 3 kubki",
            difficulty: "easy",
            text: "Wyzwanie wymagające koncentracji i pamięci wzrokowej. Dla wielu drużyn było jednym z najpewniejszych źródeł punktów. W tej minigrze należy śledzić uważnie, gdzie jest ukryty element i wskazać, pod którym kubeczkiem został ukryty.",
            competences: ["Koncentracja", "Pamięć", "Spostrzegawczość"],
        },
        {
            title: "Tor wspinaczkowy",
            difficulty: "easy",
            text: "Zdobycie punktów i dobrego czasu w tej minigrze przyszło z łatwością większości uczestników. Polega na wspinaniu się przez wirtualny tor wspinaczkowy, gdzie na finiszu trzeba kliknąć w guzik aby wygrać. Jest doskonałą okazją do ćwiczenia poruszania się po VR oraz uczy działania pod presją czasu i zdrowej rywalizacji.",
            competences: ["Koordynacja ruchowa", "Działanie pod presją czasu"],
        },
        {
            title: "Gdzie to jest?",
            difficulty: "hard",
            text: "Jedno z największych wyzwań turnieju. Polega na oznaczeniu znanych miejsc na globusie. Uczestnik dzięki temu sprawdza swoją wiedzę geograficzną, pamięć oraz dokładność.",
            competences: ["Wykorzystanie wiedzy", "Pamięć", "Dokładność"],
        },
        {
            title: "Trivia",
            difficulty: "hard",
            text: "Zadanie polegające na rozwiązaniu quizu z wiedzy ogólnej. Dzięki czemu można sprawdzić, czy uczestnik posiada wiedzę z zakresu m.in. grafiki komputerowej, gastronomii, geografii, historii a także ze świata micree. Uczy szybkiego podejmowania decyzji.",
            competences: ["Wykorzystanie wiedzy", "Pamięć", "Podejmowanie decyzji"],
        },
        {
            title: "Sudoku",
            difficulty: "hard",
            text: "Najbardziej logiczne z wyzwań. Trudne, ale dające satysfakcję po ukończeniu. W sudoku chodzi o to, żeby w każdym wierszu i kolumnie nie powtarzała się żadna liczba. Idealne zadanie do sprawdzenia logicznego myślenia ale i pobudzające wyobraźnię.",
            competences: ["Logiczne myślenie", "Koncentracja", "Wyobraźnia"],
        },
        {
            title: "Cztery Kształty",
            difficulty: "medium",
            text: "Pozornie proste zadanie wymagające spostrzegawczości. Należy w nim złapać cylinder oraz stożek i włożyć je jednocześnie w dziurki. Następnie podobnie włożyć dwie pozostałe bryły. Przy czym trzeba pamiętać, aby każdą bryłę złapać w odpowiedniej kolejności. Dzięki temu zadaniu uczestnik sprawdza swoją kreatywność, wyobraźnię, spostrzegawczość i spryt.",
            competences: ["Spostrzegawczość", "Wyobraźnia", "Kreatywność", "Koordynacja ruchowa", "Pamięć"],
        },
        {
            title: "Muzyczny Refleks",
            difficulty: "medium",
            text: "Minigra, w której najlepiej radziły sobie osoby z dobrym wyczuciem rytmu. Zadaniem w niej jest trafić w nuty w odpowiednim momencie. Wymaga zręczności i spostrzegawczości.",
            competences: ["Refleks", "Koordynacja ruchowa", "Spostrzegawczość", "Koncentracja"],
        },
        {
            title: "Kosmiczny tor przeszkód",
            difficulty: "hard",
            text: "Typowe zadanie na zręczność i umiejętność szybkiego reagowania. Polega na omijaniu asteroid przez cały czas gry.",
            competences: ["Refleks", "Koordynacja ruchowa", "Koncentracja"],
        },
        {
            title: "Wyzwanie Stolarza",
            difficulty: "hard",
            text: "Wyzwanie wymagające cierpliwości i dokładności. Sprawdza podstawową wiedzę z zakresu stolarki budowlanej. Dostępne są takie narzędzia jak zakrętarka, młotek, wkrętarka, deski czy nakrętki, których trzeba odpowiednio użyć.",
            competences: ["Cierpliwość", "Dokładność", "Wykorzystanie wiedzy"],
        },
    ];

    const filteredGames =
        filter === "all"
            ? games
            : games.filter((game) => game.difficulty === filter);

    const chartGames = selectedGame === "all"
        ? filteredGames
        : filteredGames.filter((game) => game.title === selectedGame);

    const pieData = Object.entries(competenceColors)
        .map(([competence, color]) => {
            const matchingGames = chartGames.filter((game) =>
                game.competences.includes(competence)
            );
            return {
                id: competence,
                label: competence,
                value: matchingGames.length,
                color,
                games: matchingGames.map((game) => game.title),
            };
        })
        .filter((item) => item.value > 0);

    const changeFilter = (value) => {
        setFilter(value);
        setSelectedGame("all");
    };

    const easy = games.filter((g) => g.difficulty === "easy").length;
    const medium = games.filter((g) => g.difficulty === "medium").length;
    const hard = games.filter((g) => g.difficulty === "hard").length;

    const difficultyConfig = {
        easy: {
            label: "Łatwa",
            color: "success",
            border: "#22c55e",
        },
        medium: {
            label: "Średnia",
            color: "warning",
            border: "#f59e0b",
        },
        hard: {
            label: "Trudna",
            color: "error",
            border: "#ef4444",
        },
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
                backgroundColor: "#294e6b",
            }}
        >

            <Typography
                variant="overline"
                sx={{
                    color: "#fff",
                    fontWeight: 700,
                    letterSpacing: 1.5,
                }}
            >
                ANALIZA MINIGIER
            </Typography>

            <Typography
                variant="h5"
                sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: "#fff"
                }}
            >
                Jak uczestnicy poradzili sobie z wyzwaniami?
            </Typography>

            <Alert
                severity="info"
                sx={{
                    mb: 3,
                    borderRadius: 3,
                }}
            >
                Uczestnicy najlepiej radzili sobie z wyzwaniami opartymi na
                refleksie i spostrzegawczości. Największym wyzwaniem okazały się
                minigry logiczne wymagające analizy, planowania i cierpliwości.
            </Alert>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                    gap: 2,
                    mb: 3,
                }}
            >
                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        borderRadius: 4,
                        position: "relative",
                        overflow: "hidden",
                        minHeight: 180,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "success.50",
                    }}
                >
                    <SpeedIcon
                        sx={{
                            position: "absolute",
                            fontSize: 180,
                            color: "success.main",
                            opacity: 0.12,
                        }}
                    />

                    <Stack
                        spacing={0.5}
                        alignItems="center"
                        sx={{ position: "relative", zIndex: 1 }}
                    >
                        <Typography
                            variant="h2"
                            fontWeight={800}
                            color="success.main"
                            lineHeight={1}
                        >
                            {easy}
                        </Typography>

                        <Typography
                            variant="h6"
                            fontWeight={600}
                            color="success.main"
                            textTransform="uppercase"
                            letterSpacing={2}
                        >
                            Łatwe
                        </Typography>
                    </Stack>
                </Paper>

                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        borderRadius: 4,
                        position: "relative",
                        overflow: "hidden",
                        minHeight: 180,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "warning.50",
                    }}
                >
                    <SportsEsportsIcon
                        sx={{
                            position: "absolute",
                            fontSize: 180,
                            color: "warning.main",
                            opacity: 0.12,
                        }}
                    />

                    <Stack
                        spacing={0.5}
                        alignItems="center"
                        sx={{ position: "relative", zIndex: 1 }}
                    >
                        <Typography
                            variant="h2"
                            fontWeight={800}
                            color="warning.main"
                            lineHeight={1}
                        >
                            {medium}
                        </Typography>

                        <Typography
                            variant="h6"
                            fontWeight={600}
                            color="warning.main"
                            textTransform="uppercase"
                            letterSpacing={2}
                        >
                            Średnie
                        </Typography>
                    </Stack>
                </Paper>

                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        borderRadius: 4,
                        position: "relative",
                        overflow: "hidden",
                        minHeight: 180,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "error.50",
                    }}
                >
                    <PsychologyIcon
                        sx={{
                            position: "absolute",
                            fontSize: 180,
                            color: "error.main",
                            opacity: 0.12,
                        }}
                    />

                    <Stack
                        spacing={0.5}
                        alignItems="center"
                        sx={{ position: "relative", zIndex: 1 }}
                    >
                        <Typography
                            variant="h2"
                            fontWeight={800}
                            color="error.main"
                            lineHeight={1}
                        >
                            {hard}
                        </Typography>

                        <Typography
                            variant="h6"
                            fontWeight={600}
                            color="error.main"
                            textTransform="uppercase"
                            letterSpacing={2}
                        >
                            Trudne
                        </Typography>
                    </Stack>
                </Paper>
            </Box>

            <Stack
                direction="row"
                spacing={1}
                sx={{
                    mb: 3,
                    flexWrap: "wrap",
                }}
            >
                <Chip
                    label="Wszystkie"
                    clickable
                    color={filter === "all" ? "primary" : "default"}
                    onClick={() => changeFilter("all")}
                    sx={{
                        color: "#fff",
                    }}
                />

                <Chip
                    label="Łatwe"
                    clickable
                    color={filter === "easy" ? "success" : "default"}
                    onClick={() => changeFilter("easy")}
                    sx={{
                        color: "#fff",
                    }}
                />

                <Chip
                    label="Średnie"
                    clickable
                    color={filter === "medium" ? "warning" : "default"}
                    onClick={() => changeFilter("medium")}
                    sx={{
                        color: "#fff",
                    }}
                />


                <Chip
                    label="Trudne"
                    clickable
                    color={filter === "hard" ? "error" : "default"}
                    onClick={() => changeFilter("hard")}
                    sx={{
                        color: "#fff",
                    }}
                />
            </Stack>

            {filteredGames.map((game) => {
                const config = difficultyConfig[game.difficulty];

                return (
                    <Accordion
                        key={game.title}
                        disableGutters
                        elevation={0}
                        sx={{
                            mb: 1,
                            borderRadius: "12px !important",
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: "divider",
                            borderLeft: `5px solid ${config.border}`,
                            "&:before": {
                                display: "none",
                            },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <Typography fontWeight={600}>
                                    {game.title}
                                </Typography>

                                <Chip
                                    size="small"
                                    label={config.label}
                                    color={config.color}
                                />
                            </Stack>
                        </AccordionSummary>

                        <AccordionDetails>
                            <Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: "wrap", mb: 2 }}>
                                {game.competences.map((competence) => (
                                    <Chip
                                        key={competence}
                                        size="small"
                                        label={competence}
                                        sx={{ bgcolor: competenceColors[competence], color: "#172b3a", fontWeight: 600 }}
                                    />
                                ))}
                            </Stack>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {game.text}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            <Paper elevation={0} sx={{ mt: 3, p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                    Zakres kompetencji w minigrach
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Wykres pokazuje liczbę gier przypisanych do danej kompetencji
                    na podstawie opisów. Jedna gra może rozwijać kilka kompetencji.
                    Dla pojedynczej gry każda kompetencja ma taką samą wagę.
                    Wykres uwzględnia wybrany poziom trudności.
                </Typography>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label="Wybierz minigrę"
                    value={selectedGame}
                    onChange={(event) => setSelectedGame(event.target.value)}
                    sx={{ maxWidth: 420, mb: 2 }}
                >
                    <MenuItem value="all">Wszystkie ({filteredGames.length})</MenuItem>
                    {filteredGames.map((game) => (
                        <MenuItem key={game.title} value={game.title}>{game.title}</MenuItem>
                    ))}
                </TextField>
                {pieData.length > 0 ? (
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 1fr)" }, gap: 2, alignItems: "center" }}>
                        <Box sx={{ height: { xs: 320, sm: 400 }, minWidth: 0 }}>
                            <ResponsivePie
                                data={pieData}
                                margin={{ top: 24, right: 24, bottom: 24, left: 24 }}
                                innerRadius={0.55}
                                padAngle={1.5}
                                cornerRadius={4}
                                colors={({ data }) => data.color}
                                borderWidth={1}
                                borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                                enableArcLinkLabels={false}
                                arcLabel={({ value }) => String(value)}
                                arcLabelsSkipAngle={10}
                                arcLabelsTextColor="#172b3a"
                                role="img"
                                ariaLabel="Liczba gier przypisanych do poszczególnych kompetencji. Dane podano również w legendzie."
                                tooltip={({ datum }) => (
                                    <Paper elevation={4} sx={{ p: 1.5, maxWidth: 280 }}>
                                        <Typography variant="body2" fontWeight={700}>{datum.label}</Typography>
                                        <Typography variant="body2">Liczba gier: {datum.value}</Typography>
                                        <Typography variant="caption">{datum.data.games.join(", ")}</Typography>
                                    </Paper>
                                )}
                            />
                        </Box>
                        <Stack component="ul" spacing={1.5} sx={{ listStyle: "none", p: 0, m: 0 }}>
                            {pieData.map((item) => (
                                <Box component="li" key={item.id}>
                                    <Chip size="small" label={item.label} sx={{ bgcolor: item.color, color: "#172b3a", fontWeight: 600 }} />
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        {/* Liczba gier: {item.value} */}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {item.games.join(", ")}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                ) : (
                    <Typography sx={{ mt: 2 }}>Brak gier dla wybranego zakresu.</Typography>
                )}
            </Paper>
        </Paper>
    );
}



// import {
//     Paper,
//     Typography,
//     Box,
//     Stack,
//     Chip,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     Alert,
// } from "@mui/material";
// import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
// import PsychologyIcon from "@mui/icons-material/Psychology";
// import SpeedIcon from "@mui/icons-material/Speed";
// import { useState } from "react";

// export default function MinigamesAnalysis() {
//     const [filter, setFilter] = useState("all");

//     const games = [
//         {
//             title: "Pacnij Kreta",
//             difficulty: "easy",
//             text: "Jedna z najlepiej ocenianych minigier. Większość zespołów osiągała wysoką skuteczność już po kilku próbach. Polega na tym, aby jak najszybciej pacnąć wszystkie wysunięte krety. Punkty zdobywa się za każde trafienie, a traci się wraz z upływem czasu. Gra ćwiczy spostrzegawczość, refleks i szybkie reagowanie.",
//             competences: ["Wsółpraca", "Kreatywność"]
//         },
//         {
//             title: "Gra w 3 kubki",
//             difficulty: "easy",
//             text: "Wyzwanie wymagające koncentracji i pamięci wzrokowej. Dla wielu drużyn było jednym z najpewniejszych źródeł punktów. W tej minigrze należy śledzić uważnie, gdzie jest ukryty element i wskazać, pod którym kubeczkiem został ukryty.",
//         },
//         {
//             title: "Tor wspinaczkowy",
//             difficulty: "easy",
//             text: "Zdobycie punktów i dobrego czasu w tej minigrze przyszło z łatwością większości uczestników. Polega na wspinaniu się przez wirtualny tor wspinaczkowy, gdzie na finiszu trzeba kliknąć w guzik aby wygrać. Jest doskonałą okazją do ćwiczenia poruszania się po VR oraz uczy działania pod presją czasu i zdrowej rywalizacji.",
//         },
//         {
//             title: "Gdzie to jest?",
//             difficulty: "hard",
//             text: "Jedno z największych wyzwań turnieju. Polega na oznaczeniu znanych miejsc na globusie. Uczestnik dzięki temu sprawdza swoją wiedzę geograficzną, pamięć oraz dokładność.",
//         },
//         {
//             title: "Trivia",
//             difficulty: "hard",
//             text: "Zadanie polegające na rozwiązaniu quizu z wiedzy ogólnej. Dzięki czemu można sprawdzić, czy uczestnik posiada wiedzę z zakresu m.in. grafiki komputerowej, gastronomii, geografii, historii a także ze świata micree. Uczy szybkiego podejmowania decyzji.",
//         },
//         {
//             title: "Sudoku",
//             difficulty: "hard",
//             text: "Najbardziej logiczne z wyzwań. Trudne, ale dające satysfakcję po ukończeniu. W sudoku chodzi o to, żeby w każdym wierszu i kolumnie nie powtarzała się żadna liczba. Idealne zadanie do sprawdzenia logicznego myślenia ale i pobudzające wyobraźnię.",
//         },
//         {
//             title: "Cztery Kształty",
//             difficulty: "medium",
//             text: "Pozornie proste zadanie wymagające spostrzegawczości. Należy w nim złapać cylinder oraz stożek i włożyć je jednocześnie w dziurki. Następnie podobnie włożyć dwie pozostałe bryły. Przy czym trzeba pamiętać, aby każdą bryłę złapać w odpowiedniej kolejności. Dzięki temu zadaniu uczestnik sprawdza swoją kreatywność, wyobraźnię, spostrzegawczość i spryt.",
//         },
//         {
//             title: "Muzyczny Refleks",
//             difficulty: "medium",
//             text: "Minigra, w której najlepiej radziły sobie osoby z dobrym wyczuciem rytmu. Zadaniem w niej jest trafić w nuty w odpowiednim momencie. Wymaga zręczności i spostrzegawczości.",
//         },
//         {
//             title: "Kosmiczny tor przeszkód",
//             difficulty: "hard",
//             text: "Typowe zadanie na zręczność i umiejętność szybkiego reagowania. Polega na omijaniu asteroid przez cały czas gry.",
//         },
//         {
//             title: "Wyzwanie Stolarza",
//             difficulty: "hard",
//             text: "Wyzwanie wymagające cierpliwości i dokładności. Sprawdza podstawową wiedzę z zakresu stolarki budowlanej. Dostępne są takie narzędzia jak zakrętarka, młotek, wkrętarka, deski czy nakrętki, których trzeba odpowiednio użyć.",
//         },
//     ];

//     const filteredGames =
//         filter === "all"
//             ? games
//             : games.filter((game) => game.difficulty === filter);

//     const easy = games.filter((g) => g.difficulty === "easy").length;
//     const medium = games.filter((g) => g.difficulty === "medium").length;
//     const hard = games.filter((g) => g.difficulty === "hard").length;

//     const difficultyConfig = {
//         easy: {
//             label: "Łatwa",
//             color: "success",
//             border: "#22c55e",
//         },
//         medium: {
//             label: "Średnia",
//             color: "warning",
//             border: "#f59e0b",
//         },
//         hard: {
//             label: "Trudna",
//             color: "error",
//             border: "#ef4444",
//         },
//     };

//     return (
//         <Paper
//             elevation={0}
//             sx={{
//                 p: 3,
//                 borderRadius: 4,
//                 border: "1px solid",
//                 borderColor: "divider",
//                 height: "100%",
//                 backgroundColor: "#294e6b",
//             }}
//         >

//             <Typography
//                 variant="overline"
//                 sx={{
//                     color: "#fff",
//                     fontWeight: 700,
//                     letterSpacing: 1.5,
//                 }}
//             >
//                 ANALIZA MINIGIER
//             </Typography>

//             <Typography
//                 variant="h5"
//                 sx={{
//                     fontWeight: 700,
//                     mb: 2,
//                     color: "#fff"
//                 }}
//             >
//                 Jak uczestnicy poradzili sobie z wyzwaniami?
//             </Typography>

//             <Alert
//                 severity="info"
//                 sx={{
//                     mb: 3,
//                     borderRadius: 3,
//                 }}
//             >
//                 Uczestnicy najlepiej radzili sobie z wyzwaniami opartymi na
//                 refleksie i spostrzegawczości. Największym wyzwaniem okazały się
//                 minigry logiczne wymagające analizy, planowania i cierpliwości.
//             </Alert>

//             <Box
//                 sx={{
//                     display: "grid",
//                     gridTemplateColumns: "repeat(3, 1fr)",
//                     gap: 2,
//                     mb: 3,
//                 }}
//             >
//                 <Paper
//                     variant="outlined"
//                     sx={{
//                         p: 3,
//                         borderRadius: 4,
//                         position: "relative",
//                         overflow: "hidden",
//                         minHeight: 180,
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         bgcolor: "success.50",
//                     }}
//                 >
//                     <SpeedIcon
//                         sx={{
//                             position: "absolute",
//                             fontSize: 180,
//                             color: "success.main",
//                             opacity: 0.12,
//                         }}
//                     />

//                     <Stack
//                         spacing={0.5}
//                         alignItems="center"
//                         sx={{ position: "relative", zIndex: 1 }}
//                     >
//                         <Typography
//                             variant="h2"
//                             fontWeight={800}
//                             color="success.main"
//                             lineHeight={1}
//                         >
//                             {easy}
//                         </Typography>

//                         <Typography
//                             variant="h6"
//                             fontWeight={600}
//                             color="success.main"
//                             textTransform="uppercase"
//                             letterSpacing={2}
//                         >
//                             Łatwe
//                         </Typography>
//                     </Stack>
//                 </Paper>

//                 <Paper
//                     variant="outlined"
//                     sx={{
//                         p: 3,
//                         borderRadius: 4,
//                         position: "relative",
//                         overflow: "hidden",
//                         minHeight: 180,
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         bgcolor: "warning.50",
//                     }}
//                 >
//                     <SportsEsportsIcon
//                         sx={{
//                             position: "absolute",
//                             fontSize: 180,
//                             color: "warning.main",
//                             opacity: 0.12,
//                         }}
//                     />

//                     <Stack
//                         spacing={0.5}
//                         alignItems="center"
//                         sx={{ position: "relative", zIndex: 1 }}
//                     >
//                         <Typography
//                             variant="h2"
//                             fontWeight={800}
//                             color="warning.main"
//                             lineHeight={1}
//                         >
//                             {medium}
//                         </Typography>

//                         <Typography
//                             variant="h6"
//                             fontWeight={600}
//                             color="warning.main"
//                             textTransform="uppercase"
//                             letterSpacing={2}
//                         >
//                             Średnie
//                         </Typography>
//                     </Stack>
//                 </Paper>

//                 <Paper
//                     variant="outlined"
//                     sx={{
//                         p: 3,
//                         borderRadius: 4,
//                         position: "relative",
//                         overflow: "hidden",
//                         minHeight: 180,
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         bgcolor: "error.50",
//                     }}
//                 >
//                     <PsychologyIcon
//                         sx={{
//                             position: "absolute",
//                             fontSize: 180,
//                             color: "error.main",
//                             opacity: 0.12,
//                         }}
//                     />

//                     <Stack
//                         spacing={0.5}
//                         alignItems="center"
//                         sx={{ position: "relative", zIndex: 1 }}
//                     >
//                         <Typography
//                             variant="h2"
//                             fontWeight={800}
//                             color="error.main"
//                             lineHeight={1}
//                         >
//                             {hard}
//                         </Typography>

//                         <Typography
//                             variant="h6"
//                             fontWeight={600}
//                             color="error.main"
//                             textTransform="uppercase"
//                             letterSpacing={2}
//                         >
//                             Trudne
//                         </Typography>
//                     </Stack>
//                 </Paper>
//             </Box>

//             <Stack
//                 direction="row"
//                 spacing={1}
//                 sx={{
//                     mb: 3,
//                     flexWrap: "wrap",
//                 }}
//             >
//                 <Chip
//                     label="Wszystkie"
//                     clickable
//                     color={filter === "all" ? "primary" : "default"}
//                     onClick={() => setFilter("all")}
//                     sx={{
//                         color: "#fff",
//                     }}
//                 />

//                 <Chip
//                     label="Łatwe"
//                     clickable
//                     color={filter === "easy" ? "success" : "default"}
//                     onClick={() => setFilter("easy")}
//                     sx={{
//                         color: "#fff",
//                     }}
//                 />

//                 <Chip
//                     label="Średnie"
//                     clickable
//                     color={filter === "medium" ? "warning" : "default"}
//                     onClick={() => setFilter("medium")}
//                     sx={{
//                         color: "#fff",
//                     }}
//                 />


//                 <Chip
//                     label="Trudne"
//                     clickable
//                     color={filter === "hard" ? "error" : "default"}
//                     onClick={() => setFilter("hard")}
//                     sx={{
//                         color: "#fff",
//                     }}
//                 />
//             </Stack>

//             {filteredGames.map((game) => {
//                 const config = difficultyConfig[game.difficulty];

//                 return (
//                     <Accordion
//                         key={game.title}
//                         disableGutters
//                         elevation={0}
//                         sx={{
//                             mb: 1,
//                             borderRadius: "12px !important",
//                             overflow: "hidden",
//                             border: "1px solid",
//                             borderColor: "divider",
//                             borderLeft: `5px solid ${config.border}`,
//                             "&:before": {
//                                 display: "none",
//                             },
//                         }}
//                     >
//                         <AccordionSummary
//                             expandIcon={<ExpandMoreIcon />}
//                         >
//                             <Stack
//                                 direction="row"
//                                 spacing={1}
//                                 alignItems="center"
//                             >
//                                 <Typography fontWeight={600}>
//                                     {game.title}
//                                 </Typography>

//                                 <Chip
//                                     size="small"
//                                     label={config.label}
//                                     color={config.color}
//                                 />
//                             </Stack>
//                         </AccordionSummary>

//                         <AccordionDetails>
//                             <Stack direction="row" spacing={1}>
//                                 <Chip size="small" label="primary" sx={{backgroundColor: '#cd4242', color: '#fff'}} />
//                                 <Chip size="small" label="success" color="success" />
//                             </Stack>
//                             <Typography
//                                 variant="body2"
//                                 color="text.secondary"
//                             >
//                                 {game.text}
//                             </Typography>
//                         </AccordionDetails>
//                     </Accordion>
//                 );
//             })}
//         </Paper>
//     );
// }